/**
 * premiumStyle.js — shared "Collective Alignment / Aurum Edge" premium
 * card recipe used site-wide.
 *
 * Why: every page kept reinventing its own variant of "deep base color
 * + soft gold lining + glow", leading to drift. This module exports a
 * single source of truth so cards across Networker, Daily, Library, Auth,
 * Aurum, Home, etc. all share the exact same gold edge — only the base
 * accent changes per zone.
 *
 * Usage:
 *   import { premiumCard, premiumPageBg, GOLD } from '@/utils/premiumStyle';
 *
 *   <div style={premiumCard({ accent: '203,161,83', goldKiss: true })} />
 *   <div style={premiumPageBg()} className="min-h-screen" />
 */

// rgb triplets so callers compose alpha freely.
export const ACCENTS = {
  aurum:       '63,143,96',    // Aurum signature green
  networker:   '120,170,255',  // Networker calm blue
  abundance:   '203,161,83',   // Abundance gold (same hex used by old cardLift)
  performance: '88,176,154',   // Performance jade
  daily:       '212,175,55',   // Daily ritual gold
  golf:        '184,158,99',   // Augusta sunlit fairway gold
  library:     '171,144,200',  // Library soft lavender
  auth:        '215,187,122',  // Auth soft champagne
  default:     '203,161,83',
};

export const GOLD = {
  hi:  'rgba(241,210,156,0.32)', // strong lining
  mid: 'rgba(241,210,156,0.22)', // standard lining
  lo:  'rgba(241,210,156,0.12)', // subtle lining
  glow:'rgba(241,210,156,0.06)', // outer glow
};

/**
 * premiumCard — solid base tint that fades into near-black, with a soft
 * gold lining and gentle accent glow. NO multi-stop diagonal gradient
 * (those produce drifty, AI-slop looking cards).
 *
 * Pass `accent` as the zone's rgb triplet (use `ACCENTS.<zone>` for
 * convenience), `goldKiss: true` to lift the gold edge a notch (e.g.
 * cards over deep-navy backgrounds where the gold needs to punch
 * through), and `density: 'low' | 'standard' | 'high'` to control how
 * much the base accent washes the surface.
 */
export const premiumCard = ({
  accent = ACCENTS.default,
  goldKiss = false,
  density = 'standard',
} = {}) => {
  const baseAlpha = density === 'low' ? 0.04
    : density === 'high' ? 0.10
    : 0.07;
  const goldEdge = goldKiss ? GOLD.hi : GOLD.mid;
  return {
    background: `linear-gradient(180deg, rgba(${accent},${baseAlpha}) 0%, rgba(10,10,8,1) 100%)`,
    border: `1px solid ${goldEdge}`,
    boxShadow:
      `0 0 32px rgba(${accent},0.06),` +
      ` 0 0 0 1px ${GOLD.glow},` +
      ` inset 0 1px 0 rgba(241,210,156,0.04)`,
  };
};

/**
 * premiumPageBg — the deep navy → near-black gradient used by Collective
 * Alignment + the Private Suite. Drop on a `min-h-screen` container.
 */
export const premiumPageBg = () => ({
  background: 'linear-gradient(180deg, #1f3a5c 0%, #0c1a30 55%, #03060f 100%)',
});

/**
 * premiumDivider — the soft horizontal gold rule used under headings.
 * Use as inline `style` on a thin `<div>` (or `<motion.div>`).
 */
export const premiumDivider = () => ({
  height: '1px',
  background: 'linear-gradient(90deg, transparent, rgba(217,180,120,0.65), transparent)',
});

/**
 * premiumHeading — typography style for Cormorant Garamond headings.
 * Apply directly via the `style` prop alongside Tailwind size classes.
 */
export const premiumHeading = {
  fontFamily: '"Cormorant Garamond", Georgia, serif',
  fontWeight: 400,
  letterSpacing: '-0.01em',
};

/**
 * premiumEyebrow — small gold uppercase eyebrow above premium headings.
 */
export const premiumEyebrow = {
  color: 'rgba(241,210,156,0.65)',
  fontSize: '10px',
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  fontWeight: 300,
};

export default premiumCard;
